import type { SortingState } from "@circleco/compass/components/DataTable";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { isEmpty, omit } from "lodash";
import { useCallback } from "react";
import { z } from "zod";

export interface FilterParams {
  closed?: boolean;
  live_at?: {
    gte?: number;
    lte?: number;
  };
  name?: string;
  recording?: boolean;
  room_type?: string;
  status?: string;
}

export type OrderParam = Record<string, "asc" | "desc">;

const optionalBooleanParam = z.boolean().optional().catch(undefined);
const optionalStringParam = z.string().optional().catch(undefined);

export const queryParamsSchema = z.object({
  page: z
    .number()
    .nullable()
    .optional()
    .catch(undefined)
    .transform(value =>
      typeof value === "number" && Number.isFinite(value)
        ? Math.max(value, 1)
        : undefined,
    ),
  filter: z
    .object({
      closed: optionalBooleanParam,
      live_at: z
        .object({
          gte: z.number().optional().catch(undefined),
          lte: z.number().optional().catch(undefined),
        })
        .optional()
        .catch(undefined),
      name: optionalStringParam,
      recording: optionalBooleanParam,
      room_type: optionalStringParam,
      status: optionalStringParam,
    })
    .nullable()
    .optional()
    .catch(undefined),
  order: z
    .record(z.string(), z.unknown())
    .nullable()
    .optional()
    .catch(undefined)
    .transform((value): OrderParam | undefined => {
      if (!value || typeof value !== "object") return undefined;

      const filtered: OrderParam = {};
      for (const [key, val] of Object.entries(value)) {
        if (val === "asc" || val === "desc") {
          filtered[key] = val;
        }
      }

      return Object.keys(filtered).length > 0 ? filtered : undefined;
    }),
});

export interface LiveStreamsSearchParams {
  filter?: FilterParams | null;
  order?: OrderParam | null;
  page?: number | null;
}

const validateLiveStreamsSearchParams = (
  search: Record<string, unknown>,
): LiveStreamsSearchParams => queryParamsSchema.parse(search);

export interface UseFilterParamsReturn {
  filter: FilterParams;
  hasActiveFilters: boolean;
  nextPage: () => void;
  order: OrderParam;
  page: number;
  prevPage: () => void;
  updateFilter: (filter: FilterParams) => void;
  updateOrder: (sortingState: SortingState) => void;
  updatePage: (newPage: number) => void;
}

export const useFilterParams = (): UseFilterParamsReturn => {
  // `strict: false` (not a route-pinned `from`) - this hook is also called
  // from `LiveStreamsFiltersBar`, which renders inline as normal page
  // content and unmounts with the route like everything else, so the edge
  // case this was originally added for (a since-removed mobile-shell
  // override slot re-rendering with stale content for a frame after
  // navigating away) no longer applies. Left as `strict: false` anyway since
  // it's a strictly safer default either way, and
  // `validateLiveStreamsSearchParams` below still gives us safe defaults.
  const rawSearch = useSearch({ strict: false });
  const search = validateLiveStreamsSearchParams(
    rawSearch as Record<string, unknown>,
  );
  const navigate = useNavigate();

  const page = search.page ?? 1;
  const filter: FilterParams = search.filter ?? {};
  const hasActiveFilters = !isEmpty(omit(filter, "closed"));
  const order: OrderParam = search.order ?? {};

  const updatePage = useCallback(
    (newPage: number) => {
      void navigate({
        from: "/settings/live_streams",
        search: (prev: LiveStreamsSearchParams) => ({
          ...prev,
          page: newPage === 1 ? undefined : newPage,
        }),
      });
    },
    [navigate],
  );

  const updateFilter = useCallback(
    (newFilter: FilterParams) => {
      void navigate({
        from: "/settings/live_streams",
        search: (prev: LiveStreamsSearchParams) => ({
          ...prev,
          page: undefined,
          filter: isEmpty(newFilter) ? undefined : newFilter,
        }),
      });
    },
    [navigate],
  );

  const updateOrder = useCallback(
    (sortingState: SortingState) => {
      const newOrder: OrderParam = {};
      sortingState.forEach(sort => {
        if (sort.id) {
          newOrder[sort.id] = sort.desc ? "desc" : "asc";
        }
      });

      void navigate({
        from: "/settings/live_streams",
        search: (prev: LiveStreamsSearchParams) => ({
          ...prev,
          page: undefined,
          order: isEmpty(newOrder) ? undefined : newOrder,
        }),
      });
    },
    [navigate],
  );

  const nextPage = useCallback(() => {
    updatePage(page + 1);
  }, [updatePage, page]);

  const prevPage = useCallback(() => {
    updatePage(Math.max(page - 1, 1));
  }, [updatePage, page]);

  return {
    page,
    filter,
    hasActiveFilters,
    order,
    updateOrder,
    updatePage,
    updateFilter,
    nextPage,
    prevPage,
  };
};
